import React, { useState, useEffect } from 'react';
import { Tooltip, OverlayTrigger } from 'react-bootstrap';
import axios from 'axios';
import { VirtualTourProvider } from '@bb/domain/dist/types/AppointmentConfig';
import errorReporter from '@app/errorReporter';
import Toggle from '../../react-common/Toggle';
import { virtualTourUrlBuilder } from '../../helpers';

type VirtualTourProviderInfo = {
  label: string;
  value: string;
  buildUrl(identifier: string): string;
  instruction: string;
};

export type VirtualTourData = {
  enabled: boolean;
  provider: VirtualTourProvider;
  identifier: string;
  valid: boolean;
};

type Providers = {
  provider: VirtualTourProvider;
  src: string;
  identifier: string;
};

type VirtualShowingSettingsProps = {
  virtualTourEnabled: boolean;
  virtualTourProvider?: VirtualTourProvider;
  virtualTourIdentifier?: string;
  setVirtualTourData: (data: VirtualTourData) => void;
};

const virtualTourProviders: Record<
  VirtualTourProvider,
  VirtualTourProviderInfo
> = {
  matterport: {
    label: 'Matterport',
    value: 'matterport',
    buildUrl: identifier =>
      virtualTourUrlBuilder({ type: 'matterport', identifier }),
    instruction: `An original Matterport link is required. The link format will look like this:
        https://my.matterport.com/show?m=gbN9sVmTcAr - If your link looks different, please speak with your Tour Provider.`,
  },
  iguide: {
    label: 'iGUIDE',
    value: 'iguide',
    buildUrl: identifier =>
      virtualTourUrlBuilder({ type: 'iguide', identifier }),
    instruction: `An original iGUIDE link is required. The link format will look like this:
        https://unbranded.youriguide.com/gbN9sVmTcAr - If your link looks different, please speak with your Tour Provider.`,
  },
  realvision: {
    label: 'Realvision',
    value: 'realvision',
    buildUrl: identifier =>
      virtualTourUrlBuilder({ type: 'realvision', identifier }),
    instruction: `An original Realvision link is required. The link format will look like this:
        https://real.vision/200-w-portland-st-1117 - If your link looks different, please speak with your Tour Provider.`,
  },
};

const VirtualShowingSettings = ({
  virtualTourEnabled,
  virtualTourProvider,
  virtualTourIdentifier,
  setVirtualTourData: _setVirtualTourData,
}: VirtualShowingSettingsProps) => {
  const [virtualTourData, setVirtualTourData] = useState<VirtualTourData>({
    enabled: virtualTourEnabled,
    provider: virtualTourProvider || 'matterport',
    identifier: virtualTourIdentifier || '',
    valid: !!(virtualTourProvider && virtualTourIdentifier),
  });
  const [virtualTourUrl, setVirtualTourUrl] = useState(
    virtualTourProviders[virtualTourProvider]?.buildUrl(virtualTourIdentifier),
  );
  const [validatingVirtualTourUrl, setValidatingVirtualTourUrl] =
    useState(false);
  const [spatula, setSpatula] = useState<string>();

  useEffect(() => {
    const fetchSpatula = async () => {
      const {
        data: { spatulaDomain },
      } = await axios.get<{ spatulaDomain: string }>('/secrets/spatula_domain');
      setSpatula(spatulaDomain);
    };
    fetchSpatula();
  }, []);

  useEffect(() => {
    if (!virtualTourData.valid || !virtualTourData.enabled) {
      _setVirtualTourData({
        ...virtualTourData,
        provider: undefined,
        identifier: '',
      });
    } else {
      _setVirtualTourData(virtualTourData);
    }
  }, [_setVirtualTourData, virtualTourData]);

  const assertNever = (type: never) => {
    // noop
  };

  /**
   * Simple validation logic for virtual tour URL, just checking the domain
   * @param provider
   * @param url
   */
  const preliminaryValidateVirtualTourURL = (
    provider: VirtualTourProvider,
    path: string,
  ) => {
    let url: URL;
    try {
      url = new URL(path);
    } catch {
      return false;
    }
    switch (provider) {
      case 'iguide':
        return url.hostname.endsWith('youriguide.com');
      case 'matterport':
        return url.hostname.endsWith('matterport.com');
      case 'realvision':
        return url.hostname.endsWith('real.vision');
      default:
        assertNever(provider);
        return true;
    }
  };

  const validateVirtualTourUrl = async () => {
    if (
      !preliminaryValidateVirtualTourURL(
        virtualTourData.provider,
        virtualTourUrl,
      )
    ) {
      setVirtualTourData(prev => ({ ...prev, valid: false }));
      // If the user entered the wrong domain, don’t bother to proceed to ask the server for validation.
      return;
    }

    setValidatingVirtualTourUrl(true);

    try {
      const { data } = await axios.get<{ providers: Providers[] }>(
        `${spatula}/parse?url=${encodeURIComponent(virtualTourUrl)}`,
        {
          transformRequest: [
            (_data, headers) => {
              // eslint-disable-next-line no-param-reassign
              delete headers['bb-web-bundle-version'];
              return _data;
            },
          ],
        },
      );

      if (data?.providers?.length && data.providers[0]?.identifier) {
        const [{ provider, identifier }] = data.providers;
        setVirtualTourData({
          enabled: true,
          provider,
          identifier,
          valid: true,
        });
      } else {
        setVirtualTourData(prev => ({ ...prev, valid: false }));
      }
    } catch (e) {
      errorReporter.error(e);
    } finally {
      setValidatingVirtualTourUrl(false);
    }
  };

  const virtualTourUrlSubtext = () => {
    const { valid } = virtualTourData;

    if (validatingVirtualTourUrl) {
      return (
        <>
          <i className="fa fa-spinner fa-spin"></i> Validating
        </>
      );
    }
    if (valid) {
      return (
        <label className="font-normal text-success m-t-xs">
          &nbsp;&#10003; Virtual Tour Embed URL is valid.
        </label>
      );
    }
    if (valid === false) {
      return (
        <label className="error">
          Virtual Tour Embed URL is invalid or not assigned to a listing.
        </label>
      );
    }
    if (virtualTourUrl.length >= 6 || virtualTourUrl.length === 0) {
      return (
        <label className="error">
          Please validate the Virtual Tour Embed URL.
        </label>
      );
    }
  };

  return (
    <div className="row m-b-xl">
      <div className="col-md-7 col-lg-12 m-t-md p-l">
        <legend className="p-b-xs">Virtual Showing Settings</legend>
        <div className="col-sm-3 p-x-lg">
          <div className="display-flex justify-space-between align-items-start">
            <label>3D Virtual Showings</label>
            <Toggle
              onClick={() => {
                setVirtualTourUrl('');
                setVirtualTourData(prev => ({
                  ...prev,
                  enabled: !prev.enabled,
                  valid: undefined,
                }));
              }}
              model={virtualTourEnabled}
              dataCy="toggle-virtual-tour"
            />
          </div>
        </div>

        {virtualTourEnabled && (
          <>
            <div className="col-sm-3 p-x-lg">
              <label className="m-b-xs">3D Virtual Tour Type:</label>
              <div>
                <select
                  className="form-control"
                  value={virtualTourData.provider}
                  onChange={e => {
                    const provider = e.target.value as VirtualTourProvider;
                    setVirtualTourUrl('');
                    setVirtualTourData(prev => ({
                      ...prev,
                      provider,
                      valid: undefined,
                    }));
                  }}
                  data-cy="virtual-tour-provider-dropdown"
                >
                  {Object.keys(virtualTourProviders).map(
                    (key: VirtualTourProvider) => {
                      const provider = virtualTourProviders[key];
                      return (
                        <option
                          key={key}
                          value={key}
                          label={provider.label}
                          data-cy={`virtual-tour-provider-option-${key}`}
                        >
                          {provider.label}
                        </option>
                      );
                    },
                  )}
                </select>
              </div>
            </div>

            <div className="col-sm-6 p-x-lg">
              <label className="m-b-xs" style={{ height: '13px' }}></label>
              <div className="input-group display-flex justify-space-between align-items-center">
                <input
                  type="text"
                  className="form-control"
                  size={50}
                  value={virtualTourUrl}
                  name="virtual_tour_url"
                  placeholder="Virtual Tour Embed URL"
                  disabled={validatingVirtualTourUrl}
                  onChange={e => {
                    setVirtualTourUrl(e.target.value);
                    setVirtualTourData(prev => ({ ...prev, valid: undefined }));
                  }}
                  data-cy="virtual-tour-url-input"
                />
                <span>
                  <button
                    className="btn btn-default"
                    onClick={() => {
                      validateVirtualTourUrl();
                    }}
                    disabled={
                      validatingVirtualTourUrl ||
                      !virtualTourUrl ||
                      virtualTourUrl.length < 6 ||
                      virtualTourData.valid
                    }
                    data-cy="virtual-tour-url-validate-button"
                  >
                    Validate
                  </button>
                </span>
                <OverlayTrigger
                  placement="left"
                  overlay={
                    <Tooltip id={`${virtualTourProvider}-instructions-tooltip`}>
                      {
                        virtualTourProviders[virtualTourData.provider]
                          ?.instruction
                      }
                    </Tooltip>
                  }
                >
                  <i className="fa fa-info-circle m-l-sm font-size-16"></i>
                </OverlayTrigger>
              </div>
              {virtualTourUrlSubtext()}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default VirtualShowingSettings;
